/**
 * DefectsSettingsView.js
 *
 * Full-screen settings view for Defects module configuration.
 * WBC3 Edition: 4 settings for defect management
 *
 * Settings:
 * 1. Default Risk Level - Medium (default), Low, High, Critical
 * 2. Resolution Target Days - Default 30, range 1-365
 * 3. Require Corrective Action on Close - Default ON (WBC3 4.11.5 requirement)
 * 4. Show Resolved Defects - Toggle visibility
 *
 * Pattern: Full-screen overlay accessed via gear icon (same as Crew Settings)
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  Eye,
  Info,
  RotateCcw,
  Check,
  Shield
} from 'lucide-react';
import { useDefectsConfig, RISK_LEVELS } from '../../../../hooks/domain/defects/useDefectsConfig';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

/**
 * Section wrapper component
 */
const SettingsSection = ({ icon: Icon, title, description, children, regulatory = false }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="bg-gray-900 px-5 py-3">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-cyan-400" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            {regulatory && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded">
                WBC3
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-slate-300 mt-0.5">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

/**
 * Toggle switch component
 */
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
      border-2 border-transparent transition-colors duration-200 ease-in-out
      focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2
      ${enabled ? 'bg-cyan-600' : 'bg-slate-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full
        bg-white shadow ring-0 transition duration-200 ease-in-out
        ${enabled ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);

/**
 * Number input with range
 */
const NumberInput = ({ value, onChange, min, max, unit = 'days' }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const clamped = Math.min(max, Math.max(min, localValue));
    setLocalValue(clamped);
    if (clamped !== value) {
      onChange(clamped);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <input
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(parseInt(e.target.value) || min)}
        onBlur={handleBlur}
        min={min}
        max={max}
        className="w-20 px-3 py-2 text-sm border border-slate-300 rounded-lg
          focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
      <span className="text-sm text-slate-500">{unit}</span>
      <span className="text-xs text-slate-400">({min}-{max})</span>
    </div>
  );
};

/**
 * Risk level selection component
 */
const RiskLevelSelect = ({ value, onChange }) => {
  const levels = Object.values(RISK_LEVELS);

  const getColor = (level) => {
    switch (level) {
      case 'Critical': return 'border-red-500 bg-red-50';
      case 'High': return 'border-orange-500 bg-orange-50';
      case 'Medium': return 'border-yellow-500 bg-yellow-50';
      case 'Low': return 'border-blue-500 bg-blue-50';
      default: return 'border-slate-300 bg-white';
    }
  };

  const getTextColor = (level) => {
    switch (level) {
      case 'Critical': return 'text-red-700';
      case 'High': return 'text-orange-700';
      case 'Medium': return 'text-yellow-700';
      case 'Low': return 'text-blue-700';
      default: return 'text-slate-700';
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {levels.map((level) => (
        <button
          key={level.id}
          onClick={() => onChange(level.id)}
          className={`
            p-3 rounded-lg border-2 text-left transition-all
            ${value === level.id
              ? `${getColor(level.id)} ring-2 ring-offset-1 ring-cyan-500`
              : 'border-slate-200 bg-white hover:bg-slate-50'
            }
          `}
        >
          <div className={`font-medium ${value === level.id ? getTextColor(level.id) : 'text-slate-700'}`}>
            {level.label}
          </div>
          <div className="text-xs text-slate-500 mt-1">{level.description}</div>
        </button>
      ))}
    </div>
  );
};

/**
 * Main DefectsSettingsView component
 */
const DefectsSettingsView = ({ onBack }) => {
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  const {
    defaultRiskLevel,
    resolutionTargetDays,
    requireCorrectiveActionOnClose,
    showResolvedDefects,
    setDefaultRiskLevel,
    setResolutionTargetDays,
    setRequireCorrectiveActionOnClose,
    setShowResolvedDefects,
    resetToDefaults,
    lastModified,
    modifiedBy
  } = useDefectsConfig();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Show feedback message
  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  // Handle reset
  const handleReset = async () => {
    await resetToDefaults();
    setShowResetConfirm(false);
    showFeedback('Settings reset to defaults');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Defects Settings</h1>
                <p className="text-sm text-slate-500">Configure defect management for this vessel</p>
              </div>
            </div>

            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600
                hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`
          fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg
          ${feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
        `}>
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {feedback.message}
          </div>
        </div>
      )}

      {/* Reset Confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset to Defaults?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will reset all defect settings to their default values. This action is logged.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Reset Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Audit Notice */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Audit Trail Active</p>
            <p className="mt-0.5">
              All changes are logged with your name and timestamp for SMS compliance.
            </p>
          </div>
        </div>

        {/* Section 1: Default Risk Level */}
        <SettingsSection
          icon={AlertTriangle}
          title="Default Risk Level"
          description="Set the default risk level for new defects"
        >
          <p className="text-sm text-slate-600 mb-4">
            New defects will be assigned this risk level by default. This can be changed when recording the defect.
          </p>
          <RiskLevelSelect
            value={defaultRiskLevel}
            onChange={setDefaultRiskLevel}
          />
        </SettingsSection>

        {/* Section 2: Resolution Target */}
        <SettingsSection
          icon={Clock}
          title="Resolution Target"
          description="Default target days for defect resolution"
        >
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Target Resolution Time
            </label>
            <p className="text-sm text-slate-500 mb-3">
              Set the default number of days to resolve defects. This helps track overdue defects.
            </p>
            <NumberInput
              value={resolutionTargetDays}
              onChange={setResolutionTargetDays}
              min={1}
              max={365}
            />
          </div>
        </SettingsSection>

        {/* Section 3: Require Corrective Action (REGULATORY) */}
        <SettingsSection
          icon={Shield}
          title="Require Corrective Action on Close"
          description="WBC3 Section 4.11.5 requires documented corrective actions"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Enforce Corrective Action Documentation</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, users must document the root cause and corrective action taken
                before a defect can be closed. This is required by WBC3 regulations for audit compliance.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> WBC3 Section 4.11.5 requires that all defects
                  have documented corrective actions to prevent recurrence. Disabling this setting
                  may affect audit compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={requireCorrectiveActionOnClose}
              onChange={setRequireCorrectiveActionOnClose}
            />
          </div>
        </SettingsSection>

        {/* Section 4: Show Resolved Defects */}
        <SettingsSection
          icon={Eye}
          title="Resolved Defects Visibility"
          description="Show or hide closed defects in the main list"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Show Resolved Defects</p>
              <p className="text-sm text-slate-500 mt-0.5">
                When enabled, closed/resolved defects will appear in the main defects list.
                You can always access resolved defects in the Defect History tab.
              </p>
            </div>
            <Toggle
              enabled={showResolvedDefects}
              onChange={setShowResolvedDefects}
            />
          </div>
        </SettingsSection>

        {/* Last Modified */}
        {lastModified && (
          <div className="text-xs text-slate-500 text-center pt-4">
            Last modified: {new Date(lastModified).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            {modifiedBy?.name && ` by ${modifiedBy.name}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default DefectsSettingsView;
